<script lang="ts" setup>
import type { ConfiguratorPool } from '~/models/configurator/configuratorPool.model'

const props = defineProps<{
  title: string
  description: string
  form: string
  currentPool: ConfiguratorPool | null
}>()

const gtm = useGtm()
const { $toast, $i18n } = useNuxtApp()
const { t } = useI18n()
const { dt } = useDirectusTranslation()

const selectedConfiguration = computed(() => {
  return [
    {
      label: t('label.pool_line'),
      value: dt(props.currentPool?.poolLine.translations)?.name,
    },
    {
      label: t('label.size'),
      value: props.currentPool?.size?.size,
    },
    {
      label: t('label.stair'),
      value: dt(props.currentPool?.stair.translations)?.name,
    },

    {
      label: t('label.color'),
      value: dt(props.currentPool?.color.translations)?.name,
    },
  ]
})

const open = defineModel('open', { type: Boolean, default: false })

const { data: form } = await useForm(props.form)

const formData = ref<any>({})

const domParser = import.meta.client ? new DOMParser() : null
const isSubmitting = ref(false)

const formSubmitData = computed(() => {
  const data: {
    [key: string]: {
      type: string
      value: any
      label: string
    }
  } = {}
  if (!form.value?.form_inputs)
    return data

  data.configuration = {
    type: 'form_configuration',
    value: selectedConfiguration.value,
    label: t('label.your_configuration'),
  }

  for (const input of form.value?.form_inputs) {
    if (input.collection === 'form_divider')
      continue

    const dataObject = {
      type: input.collection,
      value: formData.value[input.item.key],
      label: '',
    }
    if (input.collection === 'form_special_dealer_selector')
      dataObject.label = input.item.key
    else
      dataObject.label = domParser?.parseFromString(dt(input.item.translations)?.label ?? '', 'text/html').body.textContent ?? ''

    data[input.item.key as string] = dataObject
  }
  return {
    form_id: form.value?.id,
    data,
    language: $i18n.locale.value,
  }
})

const id = useId()
async function handleSubmit() {
  try {
    if (isSubmitting.value)
      return
    isSubmitting.value = true
    const _result = await $fetch('/server/form', {
      method: 'POST',
      body: formSubmitData.value,
    })
    formData.value = {}
    $toast(t('form.success.title'))

    open.value = false

    if (form.value?.form_gtm_id) {
      gtm.proxy.dataLayer.push({
        event: 'form_submission',
        form_id: form.value.form_gtm_id,
        language: $i18n.locale.value,
        success: true,
      })
    }
  }
  catch (error) {
    console.error(error)
    $toast.error(t('form.error.title'), {
      description: t('form.error.description'),
    })
  }
  finally {
    isSubmitting.value = false
  }
}

const submitClasses = computed(() => {
  if (isSubmitting.value) {
    return 'opacity-50 mt-3rem'
  }
  return 'mt-3rem'
})
</script>

<template>
  <RadixDialogRoot v-model:open="open">
    <RadixDialogPortal>
      <RadixDialogOverlay class="DialogOverlay">
        <RadixDialogContent
          class="DialogContent"
          @interact-outside="(e: Event) => {
            e.preventDefault()
          }"
        >
          <RadixDialogTitle class="text-4rem font-title">
            {{ props.title }}
          </RadixDialogTitle>
          <RadixDialogDescription class="mb-2rem mt-1rem whitespace-pre-line text-1.5rem">
            {{ props.description }}
          </RadixDialogDescription>
          <div>
            <!-- CONTENT OF MODAL -->
            <div class="flex flex-col">
              <TextWithAfterLine :text="t('label.your_configuration')" />
              <div class="grid grid-cols-[min-content_1fr] mx-auto my-2rem gap-x-1.6rem gap-y-1.2rem border border-neutral-gray bg-neutral-white/10 px-2rem py-1rem text-1.2rem">
                <template v-for="info in selectedConfiguration" :key="info.label">
                  <div class="whitespace-nowrap text-right">
                    {{ info.label }}
                  </div>
                  <div class="whitespace-nowrap font-bold">
                    {{ info.value }}
                  </div>
                </template>
              </div>
            </div>
            <FormKit
              :id="id"
              v-model="formData"
              :name="`form-${id}`"
              type="form"
              class="my-auto w-full"
              :submit-label="dt(form?.submit_translations)?.text ?? ''"
              :submit-attrs="{
                outerClass: submitClasses,
                id: `submit-${id}`,
                name: `submit-${id}`,
                disabled: isSubmitting,
              }"
              :classes="{
                messages: 'mt-3rem mb-1rem',
              }"
              :disabled="isSubmitting"
              @submit="handleSubmit"
            >
              <div class="grid grid-cols-2 gap-x-2rem gap-y-3rem">
                <template v-for="input in form?.form_inputs" :key="input.id">
                  <FormDivider v-if="input.collection === 'form_divider'" :data="input.item" class="col-span-2" />
                  <FormTextInput v-else-if="input.collection === 'form_text_input'" :data="input.item" :class="input.item.width === 'full' ? 'col-span-2' : '' " />
                  <FormEmailInput v-else-if="input.collection === 'form_email_input'" :data="input.item" :class="input.item.width === 'full' ? 'col-span-2' : '' " />
                  <FormTextareaInput v-else-if="input.collection === 'form_textarea_input'" :data="input.item" class="col-span-2" />
                  <FormSingleCheckboxInput v-else-if="input.collection === 'form_single_checkbox_input'" :data="input.item" class="col-span-2" />
                  <FormAddressInput v-else-if="input.collection === 'form_address_input'" :data="input.item" class="col-span-2" />
                  <FormCheckboxGroupInput v-else-if="input.collection === 'form_checkbox_group_input'" :data="input.item" class="col-span-2" />
                </template>
              </div>
            </FormKit>
          </div>
          <div class="mt-[25px] flex justify-end">
          <!-- <RadixDialogClose as-child>
            <button
              class="h-[35px] inline-flex items-center justify-center rounded-[4px] bg-green4 px-[15px] font-semibold leading-none hover:bg-green5 focus:shadow-[0_0_0_2px] focus:shadow-green7 focus:outline-none"
            >
              Save changes
            </button>
          </RadixDialogClose> -->
          </div>
          <RadixDialogClose
            class="absolute right-1.2rem top-1.2rem size-4rem inline-flex appearance-none items-center justify-center hover:bg-neutral-white/20 focus:outline-none"
            aria-label="Close"
          >
            <div class="i-radix-icons-cross-1 size-1.6rem" />
          </RadixDialogClose>
        </RadixDialogContent>
      </RadixDialogOverlay>
    </RadixDialogPortal>
  </RadixDialogRoot>
</template>

<style scoped>
.DialogOverlay {
  @apply 'fixed inset-0 grid place-items-center overflow-y-auto bg-starline-secondary/50 backdrop-blur-lg z-300';
  animation: overlayShow 300ms cubic-bezier(0.16, 1, 0.3, 1);
}

.DialogContent {
  @apply 'max-w-[70rem] my-4rem relative bg-starline-secondary text-neutral-white border border-starline-primary p-2.5rem shadow-starline focus:outline-none';
  animation: contentShow 600ms cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes overlayShow {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes contentShow {
  from {
    opacity: 0;
    transform: translate(0%, -2%) scale(0.92);
  }
  to {
    opacity: 1;
    transform: translate(0%, 0%) scale(1);
  }
}
</style>
